/**
 * Consent banner — Google Consent Mode v2 (§11). GA4/GTM/AdWords tags fire
 * only after opt-in. No analytics cookies before consent. The banner shows
 * on first visit, persists the choice, and updates the dataLayer.
 *
 * UK GDPR Art. 7(3) — consent is withdrawable as easily as given: the
 * `reopenConsentBanner()` export lets the Footer + Settings re-open the banner
 * via a module-level event, so the component's internal state doesn't need to
 * be lifted into a context.
 * British English project.
 */
import { useEffect, useState } from 'react';
import './ConsentBanner.css';

type ConsentChoice = 'granted' | 'denied' | null;

const STORAGE_KEY = 'itk-consent';
const REOPEN_EVENT = 'itk:reopen-consent';

/** Push the consent state to the GTM/GA4 dataLayer. */
function pushConsent(choice: ConsentChoice): void {
  if (typeof window === 'undefined') return;
  const dataLayer = (window as unknown as { dataLayer?: unknown[] }).dataLayer ?? [];
  (window as unknown as { dataLayer: unknown[] }).dataLayer = dataLayer;
  dataLayer.push({
    event: 'consent_update',
    analytics_storage: choice ?? 'denied',
    ad_storage: choice ?? 'denied',
    ad_user_data: choice ?? 'denied',
    ad_personalization: choice ?? 'denied',
  });
}

/**
 * Re-open the consent banner so the user can change their cookie choice.
 * Dispatches a module-level event that the ConsentBanner component listens
 * for. Used by the Footer + Settings (Art. 7(3) — withdraw as easily as given).
 */
export function reopenConsentBanner(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
}

/** Read the current consent choice from localStorage. */
export function getConsentChoice(): ConsentChoice {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'granted' || stored === 'denied' ? (stored as ConsentChoice) : null;
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'granted' || stored === 'denied') {
      pushConsent(stored as ConsentChoice);
    } else {
      setVisible(true);
      pushConsent('denied');
    }

    // Listen for re-open requests from the Footer / Settings (Art. 7(3)).
    const handleReopen = () => setVisible(true);
    window.addEventListener(REOPEN_EVENT, handleReopen);
    return () => window.removeEventListener(REOPEN_EVENT, handleReopen);
  }, []);

  const handleChoice = (c: 'granted' | 'denied') => {
    localStorage.setItem(STORAGE_KEY, c);
    setVisible(false);
    pushConsent(c);
  };

  if (!visible) return null;

  return (
    <div className="itk-consent" role="dialog" aria-label="Cookie consent" aria-live="polite">
      <div className="itk-consent__inner">
        <p className="itk-consent__text">
          We use cookies to analyse traffic and improve your experience. You can choose which cookies to allow.
        </p>
        <div className="itk-consent__actions">
          <button type="button" className="itk-consent__btn itk-consent__btn--deny" onClick={() => handleChoice('denied')}>
            Decline
          </button>
          <button type="button" className="itk-consent__btn itk-consent__btn--grant" onClick={() => handleChoice('granted')}>
            Allow all
          </button>
        </div>
      </div>
    </div>
  );
}